import React, { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { 
  Calculator, TrendingUp, TrendingDown, AlertTriangle, Calendar,
  Plus, RefreshCw, Loader2, ChevronRight, ArrowUp, ArrowDown
} from "lucide-react";
import axios from "axios";

const Budgeting = () => {
  const { token } = useAuth();
  const [budgets, setBudgets] = useState([]);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [budgetDetails, setBudgetDetails] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [showBudgetDialog, setShowBudgetDialog] = useState(false);
  const [showLineDialog, setShowLineDialog] = useState(false);
  const [loading, setLoading] = useState(true);

  const currentYear = new Date().getFullYear();

  const [budgetForm, setBudgetForm] = useState({
    name: `Budget ${currentYear}`,
    fiscal_year: currentYear,
    period_type: "monthly",
    is_active: true
  });

  const [lineForm, setLineForm] = useState({
    account_number: "",
    period: `${currentYear}-01`,
    amount: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [budgetsRes, accountsRes] = await Promise.all([
        axios.get(`${API}/budgets`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/accounts`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setBudgets(budgetsRes.data);
      setAccounts(accountsRes.data);

      // Auto-select first active budget
      const activeBudget = budgetsRes.data.find(b => b.is_active);
      if (activeBudget) {
        fetchBudgetDetails(activeBudget.id);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBudgetDetails = async (budgetId) => {
    try {
      const [detailsRes, comparisonRes] = await Promise.all([
        axios.get(`${API}/budgets/${budgetId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/budgets/${budgetId}/vs-actual`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setBudgetDetails(detailsRes.data);
      setComparison(comparisonRes.data);
      setSelectedBudget(budgetId);
    } catch (error) {
      toast.error("Kunde inte hämta budgetdetaljer");
    }
  };

  const createBudget = async () => {
    try {
      const res = await axios.post(`${API}/budgets`, budgetForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Budget skapad");
      setShowBudgetDialog(false);
      fetchData();
      fetchBudgetDetails(res.data.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte skapa budget");
    }
  };

  const addBudgetLine = async () => {
    if (!lineForm.account_number || !lineForm.amount) {
      toast.error("Fyll i konto och belopp");
      return;
    }

    try {
      await axios.post(`${API}/budgets/${selectedBudget}/lines`, {
        ...lineForm,
        amount: parseFloat(lineForm.amount)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Budgetrad tillagd");
      setShowLineDialog(false);
      setLineForm(prev => ({ ...prev, account_number: "", amount: "" }));
      fetchBudgetDetails(selectedBudget);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte lägga till budgetrad");
    }
  };

  const getVarianceBadge = (variance, isExpense = true) => {
    // For expenses: negative variance (under budget) is good
    // For income: positive variance (over budget) is good
    const isFavorable = isExpense ? variance <= 0 : variance >= 0;
    
    if (Math.abs(variance) < 0.01) {
      return <Badge variant="secondary">På mål</Badge>;
    }
    
    return isFavorable ? (
      <Badge className="bg-green-100 text-green-700">
        <ArrowDown className="h-3 w-3 mr-1" />
        {Math.abs(variance).toLocaleString('sv-SE')} kr
      </Badge>
    ) : (
      <Badge className="bg-red-100 text-red-700">
        <ArrowUp className="h-3 w-3 mr-1" />
        {Math.abs(variance).toLocaleString('sv-SE')} kr
      </Badge>
    );
  };

  const months = [
    { value: "01", label: "Januari" }, { value: "02", label: "Februari" },
    { value: "03", label: "Mars" }, { value: "04", label: "April" },
    { value: "05", label: "Maj" }, { value: "06", label: "Juni" },
    { value: "07", label: "Juli" }, { value: "08", label: "Augusti" },
    { value: "09", label: "September" }, { value: "10", label: "Oktober" },
    { value: "11", label: "November" }, { value: "12", label: "December" }
  ];

  // Filter to expense accounts (class 4-8) for budgeting
  const expenseAccounts = accounts.filter(a => {
    const cls = parseInt(a.account_number?.[0]);
    return cls >= 4 && cls <= 8;
  });

  return (
    <div className="p-6 space-y-6" data-testid="budgeting-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Budgetering</h1>
          <p className="text-muted-foreground">Skapa budgetar och jämför med utfall</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={showBudgetDialog} onOpenChange={setShowBudgetDialog}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Ny budget
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Skapa budget</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Namn</Label>
                  <Input 
                    value={budgetForm.name}
                    onChange={(e) => setBudgetForm({ ...budgetForm, name: e.target.value })}
                    placeholder="Budget 2026"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Räkenskapsår</Label>
                    <Input 
                      type="number"
                      value={budgetForm.fiscal_year}
                      onChange={(e) => setBudgetForm({ ...budgetForm, fiscal_year: parseInt(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Periodtyp</Label>
                    <Select value={budgetForm.period_type} onValueChange={(v) => setBudgetForm({ ...budgetForm, period_type: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Månadsvis</SelectItem>
                        <SelectItem value="quarterly">Kvartalsvis</SelectItem>
                        <SelectItem value="yearly">Årsvis</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowBudgetDialog(false)}>Avbryt</Button>
                <Button onClick={createBudget}>Skapa</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Budget Summary */}
      {comparison && (
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Budgeterat totalt</p>
                  <p className="text-2xl font-bold">{comparison.summary.total_budgeted.toLocaleString('sv-SE')} kr</p>
                </div>
                <Calculator className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Faktiskt utfall</p>
                  <p className="text-2xl font-bold">{comparison.summary.total_actual.toLocaleString('sv-SE')} kr</p>
                </div>
                <TrendingUp className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Avvikelse</p>
                  <p className={`text-2xl font-bold ${comparison.summary.total_variance < 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {comparison.summary.total_variance.toLocaleString('sv-SE')} kr
                  </p>
                </div>
                {comparison.summary.total_variance < 0 ? (
                  <TrendingDown className="h-8 w-8 text-green-500" />
                ) : (
                  <TrendingUp className="h-8 w-8 text-red-500" />
                )}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Varningar</p>
                  <p className="text-2xl font-bold text-amber-600">{comparison.alerts?.length || 0}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Budget Alerts */}
      {comparison?.alerts?.length > 0 && (
        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-amber-700 dark:text-amber-400 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Budgetvarningar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {comparison.alerts.map((alert, idx) => (
                <div key={idx} className="flex justify-between items-center p-3 bg-white dark:bg-background rounded-lg">
                  <div>
                    <p className="font-medium">{alert.account_number} - {alert.account_name}</p>
                    <p className="text-sm text-muted-foreground">Period: {alert.period}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-red-600">{alert.usage_percent.toFixed(0)}% använt</p>
                    <p className="text-sm">{alert.actual.toLocaleString('sv-SE')} / {alert.budgeted.toLocaleString('sv-SE')} kr</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-4 gap-6">
        {/* Budget List */}
        <div className="col-span-1 space-y-4">
          <h3 className="font-semibold">Budgetar</h3>
          <div className="space-y-2">
            {budgets.map(budget => (
              <Card 
                key={budget.id}
                className={`cursor-pointer transition-colors ${selectedBudget === budget.id ? 'border-primary' : 'hover:bg-muted/50'}`}
                onClick={() => fetchBudgetDetails(budget.id)}
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium">{budget.name}</p>
                      <p className="text-sm text-muted-foreground">{budget.fiscal_year}</p>
                    </div>
                    {budget.is_active && <Badge>Aktiv</Badge>}
                  </div>
                </CardContent>
              </Card>
            ))}
            {budgets.length === 0 && (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  Inga budgetar skapade
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Budget Details */}
        <div className="col-span-3">
          {budgetDetails ? (
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>{budgetDetails.name}</CardTitle>
                    <CardDescription>Räkenskapsår {budgetDetails.fiscal_year} • {budgetDetails.period_type === 'monthly' ? 'Månadsvis' : budgetDetails.period_type}</CardDescription>
                  </div>
                  <Dialog open={showLineDialog} onOpenChange={setShowLineDialog}>
                    <DialogTrigger asChild>
                      <Button>
                        <Plus className="h-4 w-4 mr-2" />
                        Lägg till rad
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Lägg till budgetrad</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Konto</Label>
                          <Select value={lineForm.account_number} onValueChange={(v) => setLineForm({ ...lineForm, account_number: v })}>
                            <SelectTrigger>
                              <SelectValue placeholder="Välj konto" />
                            </SelectTrigger>
                            <SelectContent>
                              {expenseAccounts.map(a => (
                                <SelectItem key={a.account_number} value={a.account_number}>
                                  {a.account_number} - {a.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Period</Label>
                            <Select value={lineForm.period.slice(-2)} onValueChange={(v) => setLineForm({ ...lineForm, period: `${budgetDetails.fiscal_year}-${v}` })}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {months.map(m => (
                                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Belopp (kr)</Label>
                            <Input 
                              type="number"
                              value={lineForm.amount}
                              onChange={(e) => setLineForm({ ...lineForm, amount: e.target.value })}
                              placeholder="10000"
                            />
                          </div>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setShowLineDialog(false)}>Avbryt</Button>
                        <Button onClick={addBudgetLine}>Lägg till</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="p-3 text-left">Konto</th>
                        <th className="p-3 text-left">Period</th>
                        <th className="p-3 text-right">Budget</th>
                        <th className="p-3 text-right">Utfall</th>
                        <th className="p-3 text-right">Avvikelse</th>
                        <th className="p-3 text-center">Förbrukat</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparison?.comparison?.map((line, idx) => (
                        <tr key={idx} className="border-t hover:bg-muted/30">
                          <td className="p-3">
                            <span className="font-mono">{line.account_number}</span>
                            <span className="text-muted-foreground ml-2">{line.account_name}</span>
                          </td>
                          <td className="p-3">{line.period}</td>
                          <td className="p-3 text-right font-mono">{line.budgeted.toLocaleString('sv-SE')}</td>
                          <td className="p-3 text-right font-mono">{line.actual.toLocaleString('sv-SE')}</td>
                          <td className="p-3 text-right">
                            {getVarianceBadge(line.variance)}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <Progress 
                                value={Math.min((line.actual / line.budgeted) * 100, 100)} 
                                className={`h-2 ${line.actual > line.budgeted ? '[&>div]:bg-red-500' : ''}`}
                              />
                              <span className="text-xs w-12 text-right">
                                {line.budgeted > 0 ? ((line.actual / line.budgeted) * 100).toFixed(0) : 0}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {(!comparison?.comparison || comparison.comparison.length === 0) && (
                        <tr>
                          <td colSpan={6} className="p-8 text-center text-muted-foreground">
                            Inga budgetrader. Lägg till din första!
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-16 text-center text-muted-foreground">
                <Calculator className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Välj en budget eller skapa en ny</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Budgeting;
